import React from "react";
import {
  BarChart, Bar, PieChart, Pie, Cell, LineChart, Line, AreaChart, Area,
  XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";

const COLORS = ["#f59e0b", "#38bdf8", "#34d399", "#a78bfa", "#fb7185", "#facc15", "#2dd4bf", "#f472b6"];

const tooltipStyle = {
  backgroundColor: "#18181b", border: "1px solid #27272a", borderRadius: 4,
  color: "#fafafa", fontSize: 12,
};

// Stable references so recharts props don't get a new object every render.
const CHART_MARGIN = { top: 4, right: 8, left: -18, bottom: 4 };
const AXIS_TICK = { fontSize: 10 };
const BAR_CURSOR = { fill: "#ffffff08" };
const BAR_RADIUS = [2, 2, 0, 0];
const AREA_DOT = { r: 2, fill: "#f59e0b" };
const AREA_ACTIVE_DOT = { r: 4 };

export function ChartCard({ title, children, testid, action }) {
  return (
    <div data-testid={testid} className="flex flex-col rounded-sm border border-zinc-800 bg-zinc-900 p-5 ring-1 ring-white/5">
      <div className="mb-4 flex items-center justify-between">
        <h3 className="font-display text-sm font-bold tracking-tight text-zinc-200">{title}</h3>
        {action}
      </div>
      <div style={{ width: "100%", height: 240 }}>{children}</div>
    </div>
  );
}

const EmptyState = () => (
  <div className="flex h-full items-center justify-center text-xs text-zinc-600">No data yet</div>
);

export function BarViz({ data }) {
  if (!data || data.length === 0) return <EmptyState />;
  return (
    <ResponsiveContainer>
      <BarChart data={data} margin={CHART_MARGIN}>
        <CartesianGrid strokeDasharray="3 3" stroke="#27272a" vertical={false} />
        <XAxis dataKey="name" stroke="#52525b" tick={AXIS_TICK} interval={0} angle={-25} textAnchor="end" height={50} />
        <YAxis stroke="#52525b" tick={AXIS_TICK} />
        <Tooltip contentStyle={tooltipStyle} cursor={BAR_CURSOR} />
        <Bar dataKey="value" radius={BAR_RADIUS}>
          {data.map((entry, i) => <Cell key={entry.name ?? i} fill={COLORS[i % COLORS.length]} />)}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

// Grouped comparison bars — green for our brand, red for competitor (PPT cost/km style).
export function CompareBar({ data, dataKey = "cpkm", prefix = "₹", suffix = "" }) {
  if (!data || data.length === 0) return <EmptyState />;
  return (
    <ResponsiveContainer>
      <BarChart data={data} margin={CHART_MARGIN}>
        <CartesianGrid strokeDasharray="3 3" stroke="#27272a" vertical={false} />
        <XAxis dataKey="brand" stroke="#52525b" tick={AXIS_TICK} interval={0} angle={-15} textAnchor="end" height={54} />
        <YAxis stroke="#52525b" tick={AXIS_TICK} />
        <Tooltip contentStyle={tooltipStyle} cursor={BAR_CURSOR} formatter={(v) => `${prefix}${Number(v).toLocaleString()}${suffix}`} />
        <Bar dataKey={dataKey} radius={BAR_RADIUS}>
          {data.map((e, i) => <Cell key={e.brand ?? i} fill={e.is_competitor ? "#fb7185" : "#34d399"} />)}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

// Donut with center total + side legend
export function PieViz({ data }) {
  if (!data || data.length === 0) return <EmptyState />;
  const total = data.reduce((s, d) => s + (d.value || 0), 0);
  return (
    <div className="flex h-full items-center gap-4">
      <div className="relative h-full min-w-0 flex-1">
        <ResponsiveContainer>
          <PieChart>
            <Pie data={data} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={82} innerRadius={54} paddingAngle={2} stroke="none">
              {data.map((entry, i) => <Cell key={entry.name ?? i} fill={COLORS[i % COLORS.length]} />)}
            </Pie>
            <Tooltip contentStyle={tooltipStyle} />
          </PieChart>
        </ResponsiveContainer>
        <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
          <span className="font-display text-2xl font-extrabold tabular-nums text-zinc-50">{total}</span>
          <span className="overline text-zinc-500">Total</span>
        </div>
      </div>
      <ul className="flex max-h-full flex-1 flex-col gap-1.5 overflow-auto pr-1 text-xs">
        {data.map((d, i) => (
          <li key={d.name ?? i} className="flex items-center justify-between gap-2">
            <span className="flex min-w-0 items-center gap-2">
              <span className="h-2.5 w-2.5 shrink-0 rounded-sm" style={{ background: COLORS[i % COLORS.length] }} />
              <span className="truncate text-zinc-300">{d.name}</span>
            </span>
            <span className="font-mono tabular-nums text-zinc-400">{d.value}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function LineViz({ data }) {
  if (!data || data.length === 0) return <EmptyState />;
  return (
    <ResponsiveContainer>
      <AreaChart data={data} margin={CHART_MARGIN}>
        <defs>
          <linearGradient id="amberFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#f59e0b" stopOpacity={0.35} />
            <stop offset="100%" stopColor="#f59e0b" stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke="#27272a" vertical={false} />
        <XAxis dataKey="name" stroke="#52525b" tick={AXIS_TICK} tickFormatter={(d) => (typeof d === "string" ? d.slice(5) : d)} />
        <YAxis stroke="#52525b" tick={AXIS_TICK} allowDecimals={false} />
        <Tooltip contentStyle={tooltipStyle} />
        <Area type="monotone" dataKey="value" stroke="#f59e0b" strokeWidth={2} fill="url(#amberFill)" dot={AREA_DOT} activeDot={AREA_ACTIVE_DOT} />
      </AreaChart>
    </ResponsiveContainer>
  );
}
